import React, { useEffect, useRef, useState, useCallback } from 'react';

import { useTranslations } from 'next-intl';
import { AtAGlanceModel } from './AtAGlance';
import Link from '@/components/general/Link';
import classNames from 'classnames';

type AtAGlanceSummaryModel = {
  summary?: AtAGlanceModel['summary'];
};

const AtAGlanceSummary = ({ summary }: AtAGlanceSummaryModel) => {
  const t = useTranslations();

  const summaryRef = useRef<HTMLDivElement>(null);
  const [{ atTop, atBottom, canScroll }, setScrollState] = useState({
    atTop: true,
    atBottom: true,
    canScroll: true,
  });

  const measure = useCallback((el: HTMLDivElement) => {
    if (!el) return;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;
    const nextState = {
      atTop: scrollTop <= 0.5,
      atBottom: scrollHeight - clientHeight - scrollTop <= 1,
      canScroll: scrollHeight - clientHeight > 1,
    };

    setScrollState((previousState) =>
      previousState.atTop === nextState.atTop &&
      previousState.atBottom === nextState.atBottom &&
      previousState.canScroll === nextState.canScroll
        ? previousState
        : nextState,
    );
  }, []);

  const scrollAmount = useCallback((): number => {
    const el = summaryRef.current;
    if (!el) return 0;
    return el?.clientHeight * 0.3;
  }, []);

  const onScroll = useCallback<React.UIEventHandler<HTMLDivElement>>(
    (e) => {
      const el = e.currentTarget;
      measure(el);
    },
    [measure],
  );

  const up = useCallback(() => {
    const el = summaryRef.current;
    if (!el) return;
    el.scrollBy({ top: -scrollAmount(), behavior: 'smooth' });
  }, [scrollAmount]);

  const down = useCallback(() => {
    const el = summaryRef.current;
    if (!el) return;
    el.scrollBy({ top: scrollAmount(), behavior: 'smooth' });
  }, [scrollAmount]);

  useEffect(() => {
    const el = summaryRef.current;
    if (!el) return;

    // Initial measure
    measure(el);

    let animationFrame = 0;

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        measure(el);
      });
    });

    ro.observe(el);
    return () => {
      cancelAnimationFrame(animationFrame);
      ro.disconnect();
    };
  }, [measure]);

  if (!summary) return null;

  /*
report.atAGlance.$reportSections
Report Sections

report.$jumpToSection
Jump to section

report.atAGlance.$moresummarypoints
More summary points
*/
  return (
    <>
      <div
        className="scrollbar scrollbar-track-transparent scrollbar-hover:scrollbar-thumb-white scrollbar-thumb-stylus-gray-1 max-h-full w-full grow basis-0 xl:overflow-y-auto"
        data-lenis-prevent
        ref={summaryRef}
        onScroll={onScroll}
      >
        <h3 className="my-4 h-7 text-lg leading-7 font-bold">{t('report.atAGlance.$reportSections')}</h3>

        {summary.blocks.map((block, index) => (
          <div key={index} className="mt-4">
            <h4 className="text-base leading-6 font-bold">{block.heading}</h4>
            <div className="mt-4 text-base leading-6" dangerouslySetInnerHTML={{ __html: block.text }} />
            <Link className="mt-2 text-xs font-bold" link={block.anchor}>
              {t('report.$jumpToSection')}
            </Link>
          </div>
        ))}
      </div>

      <div className="flx-row mt-4 flex items-center gap-1">
        <button
          className="cursor-pointer transition-opacity disabled:cursor-default disabled:opacity-20"
          onClick={up}
          disabled={atTop || !canScroll}
        >
          <svg
            className="h-5 w-5"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            version="1.1"
            id="summPtsUpArrow"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path style={{ fill: '#808080', stroke: 'none', fillOpacity: 1 }} d="M 16,2 2,30 h 28 z" />
          </svg>
        </button>
        <button
          onClick={down}
          className="cursor-pointer transition-opacity disabled:cursor-default disabled:opacity-20"
          disabled={atBottom || !canScroll}
        >
          <svg
            className="h-5 w-5"
            width="32"
            height="32"
            viewBox="0 0 32 32"
            version="1.1"
            id="summPtsDwnArrow"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path style={{ fill: '#808080', fillOpacity: 1, stroke: 'none' }} d="M 16,30 2,2 h 28 z" />
          </svg>
        </button>
        <span className={classNames('ml-1 transition-opacity', atBottom && 'opacity-20')}>
          {t('report.atAGlance.$moresummarypoints')}
        </span>
      </div>
    </>
  );
};

export default AtAGlanceSummary;
